/**
 * ProofOfEfficiencyCard.js — The handshake-closer.
 *
 * Two modes:
 *   • Standalone (public landing) → renders the deterministic sample so
 *     visitors see "Traditional $X vs Moovin Forward $Y = Save $Z" the
 *     moment they land.
 *   • Owner-embedded → pass rent/procurement props and see your own unit.
 *
 * Institutional-grade type · dense numeric table · one closing line at
 * the bottom that reads like a handshake, not a pitch.
 */
import React, { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INK    = "#0A0F1E";
const PAPER  = "#F5F5F7";
const CHROME = "#A0A0A0";
const AMBER  = "#F59E0B";
const GREEN  = "#22c55e";
const RED    = "#ef4444";

export default function ProofOfEfficiencyCard({
  monthly_rent_usd,
  procurement_cost_usd,
  rent_cap_usd,
  waiver_eligible,
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [err, setErr]   = useState("");

  useEffect(() => {
    const custom = monthly_rent_usd != null && procurement_cost_usd != null;
    const url = custom
      ? `${BACKEND_URL}/api/proof-of-efficiency/compute`
      : `${BACKEND_URL}/api/proof-of-efficiency/sample`;
    const opts = custom
      ? {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            monthly_rent_usd, procurement_cost_usd, rent_cap_usd, waiver_eligible,
          }),
        }
      : {};
    fetch(url, opts)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(e => setErr(String(e)));
  }, [monthly_rent_usd, procurement_cost_usd, rent_cap_usd, waiver_eligible]);

  if (err) return null;
  if (!data) return null;

  const T = data.traditional;
  const M = data.moovin_forward;
  const D = data.delta;

  const $ = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const Row = ({ label, trad, mf, delta = false, bold = false }) => (
    <div style={{
      display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: 12,
      padding: "10px 14px",
      borderBottom: "1px solid rgba(160,160,160,0.14)",
      fontSize: bold ? 14 : 13, fontWeight: bold ? 800 : 500,
      color: bold ? INK : "#374151",
    }}>
      <span>{label}</span>
      <span style={{ textAlign: "right", color: delta ? RED : "#374151", fontVariantNumeric: "tabular-nums" }}>{trad}</span>
      <span style={{ textAlign: "right", color: delta ? GREEN : INK, fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{mf}</span>
    </div>
  );

  return (
    <div data-testid="proof-of-efficiency-card" style={{
      background: PAPER,
      border: "1px solid rgba(10,15,30,0.08)",
      borderRadius: 14,
      padding: compact ? 18 : 28,
      boxShadow: "0 10px 40px rgba(10,15,30,0.08)",
      maxWidth: 720, margin: "0 auto",
      color: INK, fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      {/* Header */}
      <div style={{ borderBottom: "2px solid " + INK, paddingBottom: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.24em", color: AMBER, fontWeight: 800, marginBottom: 6 }}>
          PROOF OF EFFICIENCY · INSTITUTIONAL LEDGER
        </div>
        <h2 style={{ margin: 0, fontSize: compact ? 22 : 28, fontWeight: 800, letterSpacing: "-0.01em" }}>
          The number your traditional turn costs you.
        </h2>
        <div style={{ fontSize: 13, color: CHROME, marginTop: 6 }}>
          Rent-ready delta, procurement pass-through, and pilot-waiver math — computed against your own unit, not a canned example.
        </div>
      </div>

      {/* Column headers */}
      <div style={{
        display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: 12,
        padding: "6px 14px 10px", borderBottom: "1px solid " + INK,
        fontSize: 10, letterSpacing: "0.16em", fontWeight: 800, color: CHROME, textTransform: "uppercase",
      }}>
        <span>Line Item</span>
        <span style={{ textAlign: "right" }}>{T.label}</span>
        <span style={{ textAlign: "right", color: INK }}>{M.label}</span>
      </div>

      <Row label="Turn window" trad={`${T.turn_days} days`}          mf={`${M.turn_days} days`} />
      <Row label="Procurement" trad={$(T.procurement_cost_usd)}      mf={$(M.procurement_cost_usd)} />
      <Row label={`Markup (${T.procurement_markup_pct}% vs 0%)`}     trad={$(T.procurement_markup_usd)} mf={$(M.procurement_markup_usd)} delta />
      <Row label="Vacancy loss (rent × turn days)"                  trad={$(T.vacancy_loss_usd)}       mf={$(M.vacancy_loss_usd)} delta />
      <Row
        label={M.waiver_applied ? "Stabilization fee (waived · first-unit pilot)" : "Stabilization fee"}
        trad={$(T.stabilization_fee_usd)}
        mf={M.waiver_applied ? "$0" : $(M.stabilization_fee_usd)}
        delta={M.waiver_applied}
      />
      <Row label="TOTAL OWNER COST" trad={$(T.total_owner_cost_usd)} mf={$(M.total_owner_cost_usd)} bold />

      {/* Handshake */}
      <div style={{
        marginTop: 20, padding: "16px 20px",
        background: INK, color: PAPER,
        borderRadius: 10,
      }}>
        <div style={{ fontSize: 10, letterSpacing: "0.24em", color: AMBER, fontWeight: 800, marginBottom: 6 }}>
          THE DELTA
        </div>
        <div style={{ fontSize: compact ? 26 : 34, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
          Save {$(D.savings_usd)} · {D.savings_pct}% · rent-ready {D.time_saved_days} days sooner
        </div>
        <div style={{ fontSize: 13, color: "#CBD5E1", marginTop: 10, lineHeight: 1.5 }}>
          {data.handshake_line}
        </div>
      </div>

      {/* Citations — small · confident · defensible */}
      <div style={{
        marginTop: 14, fontSize: 10, color: CHROME, lineHeight: 1.6,
        borderTop: "1px solid rgba(160,160,160,0.15)", paddingTop: 10,
      }}>
        <strong style={{ color: "#374151" }}>Cited benchmarks:</strong>{" "}
        {Object.entries(data.citations).map(([k, v]) => (
          <span key={k} style={{ marginRight: 12 }}>· {v}</span>
        ))}
      </div>
    </div>
  );
}
